import * as LocationSelectors from '@/selectors/locationSelectors';
import { fetchUserBranches } from '@/services/api/user';
import { F, INTL_ACTION, INTL_ENTITY } from '@/utils/intl';
import { ProFormItem } from '@ant-design/pro-form';
import { Spin, Tag, TreeSelect } from 'antd';
import debounce from 'lodash/debounce';
import get from 'lodash/get';
import isEmpty from 'lodash/isEmpty';
import uniqBy from 'lodash/uniqBy';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch, useSelector } from 'umi';

type BranchServiceTreeSelectProps = {
  inScope?: boolean;
  withStaff?: boolean;
  name: any;
  label?: any;
  required?: boolean;
  onChange?: (v: any) => void;
  placeholder?: React.ReactNode;
  tooltip?: React.ReactNode;
  pageSize?: number;
  fieldProps?: any;
  selectProps?: any;
  filterMode?: string;
  filterKey?: string;
  filterServices?: string[];
  agentId?: string | number;
};

const BRANCH_VALUE_PREFIX = 'branch_';
const SERVICE_VALUE_PREFIX = 'service_';

const prefixBranchValue = (id: string | number) =>
  `${BRANCH_VALUE_PREFIX}${id}`;
const prefixServiceValue = (id: string | number) =>
  `${SERVICE_VALUE_PREFIX}${id}`;

const isPrefixedBranchValue = (value: any) =>
  typeof value === 'string' && value.startsWith(BRANCH_VALUE_PREFIX);

const isPrefixedServiceValue = (value: any) =>
  typeof value === 'string' && value.startsWith(SERVICE_VALUE_PREFIX);

const unprefixValue = (value: any) => {
  if (isPrefixedBranchValue(value)) {
    return value.slice(BRANCH_VALUE_PREFIX.length);
  }
  if (isPrefixedServiceValue(value)) {
    return value.slice(SERVICE_VALUE_PREFIX.length);
  }
  return value;
};

const normalizeOutputValue = (value: any) => {
  if (Array.isArray(value)) {
    return value
      .filter((item) => !isPrefixedBranchValue(item))
      .map((item) => unprefixValue(item));
  }

  if (isPrefixedBranchValue(value)) {
    return undefined;
  }

  return unprefixValue(value);
};

const normalizeInputValue = (value: any) => {
  if (Array.isArray(value)) {
    return value.map((item) => {
      if (typeof item === 'string') {
        if (isPrefixedBranchValue(item) || isPrefixedServiceValue(item))
          return item;
        return prefixServiceValue(item);
      }

      if (typeof item === 'number') {
        return prefixServiceValue(item);
      }

      return item;
    });
  }

  if (typeof value === 'string') {
    if (isPrefixedBranchValue(value) || isPrefixedServiceValue(value))
      return value;
    return prefixServiceValue(value);
  }

  if (typeof value === 'number') {
    return prefixServiceValue(value);
  }

  return value;
};

const BranchServiceTreeSelect: React.FC<BranchServiceTreeSelectProps> = ({
  inScope = false,
  withStaff = false,
  name,
  label,
  required,
  onChange,
  placeholder,
  tooltip,
  pageSize = 30,
  agentId,
  fieldProps,
  selectProps,
  filterMode,
  filterKey,
  filterServices,
  ...props
}) => {
  const dispatch = useDispatch();

  const loading = useSelector(
    (state: DefaultRootState) =>
      state.loading.effects['location/fetchFullBranchesWithDetails'],
  );
  const fullBranchesFromStore = useSelector(
    LocationSelectors.selectFullBranches,
  );
  const allBranchesListFromStore = useSelector(
    LocationSelectors.selectAllBranchesList,
  );

  const [agentBranches, setAgentBranches] = useState<any[]>([]);
  const isAgentMode = !!agentId;

  const fullBranches = isAgentMode ? agentBranches : fullBranchesFromStore;

  const hasRemoteSearch =
    isAgentMode ||
    (allBranchesListFromStore.length > 5 &&
      fullBranches.length !== allBranchesListFromStore.length);

  const [query, setQuery] = useState('');
  const [localLoading, setLocalLoading] = useState(false);
  const [page, setPage] = useState(1);

  const abortRef = useRef<AbortController | null>(null);
  const fullBranchesRef = useRef(fullBranches);

  useEffect(() => {
    fullBranchesRef.current = fullBranches;
  }, [fullBranches]);

  useEffect(() => {
    if (isEmpty(allBranchesListFromStore)) {
      dispatch({ type: 'location/getAllBranchList' });
    }
  }, [allBranchesListFromStore, dispatch]);

  const dispatchBranchDetailsReplace = useCallback(
    (fetched: any[]) => {
      dispatch({
        type: 'location/fetchedFullBranchesList',
        payload: fetched,
      });
    },
    [dispatch],
  );

  const dispatchBranchDetailsAppend = useCallback(
    (fetched: any[]) => {
      const prev = fullBranchesRef.current || [];
      const merged = uniqBy([...prev, ...fetched], (b: any) => b.id);
      dispatch({
        type: 'location/fetchedFullBranchesList',
        payload: merged,
      });
    },
    [dispatch],
  );

  const loadPage = useCallback(
    async (nextPage: number, q: string) => {
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLocalLoading(true);

      try {
        const queryParams: any = {
          full: 1,
          in_scope: inScope ? 1 : 0,
          page_size: pageSize,
          page: nextPage,
        };

        if (!isEmpty(q)) queryParams.search = q;
        if (isAgentMode) queryParams.agent = agentId;

        const response = await fetchUserBranches({
          query: queryParams,
          signal: controller.signal,
        });

        const fetched: any[] = get(response, 'data.data', []) || [];

        if (isAgentMode) {
          if (nextPage === 1) {
            setAgentBranches(fetched);
          } else {
            setAgentBranches((prev) =>
              uniqBy([...prev, ...fetched], (b: any) => b.id),
            );
          }
        } else {
          if (nextPage === 1) {
            dispatchBranchDetailsReplace(fetched);
          } else {
            dispatchBranchDetailsAppend(fetched);
          }
        }

        setPage(nextPage);
      } catch (e) {
        setLocalLoading(false);
      } finally {
        setLocalLoading(false);
      }
    },
    [
      inScope,
      pageSize,
      agentId,
      isAgentMode,
      dispatchBranchDetailsReplace,
      dispatchBranchDetailsAppend,
    ],
  );

  const debouncedSearch = useMemo(
    () =>
      debounce((value: string) => {
        const v = value?.trim() ?? '';
        setQuery(v);
        loadPage(1, v);
      }, 300),
    [loadPage],
  );

  const handleSearch = useCallback(
    (searchValue: string) => debouncedSearch(searchValue),
    [debouncedSearch],
  );

  const handleDropdownVisibleChange = useCallback(
    (open: boolean) => {
      if (open && hasRemoteSearch && !loading && !isAgentMode) {
        dispatch({ type: 'location/fetchFullBranchesWithDetails' });
      }

      fieldProps?.onDropdownVisibleChange?.(open);
      selectProps?.onDropdownVisibleChange?.(open);
    },
    [hasRemoteSearch, loading, isAgentMode, dispatch, fieldProps, selectProps],
  );

  useEffect(() => {
    if (!isAgentMode && !loading && isEmpty(fullBranches)) {
      dispatch({ type: 'location/fetchFullBranchesWithDetails' });
    }
  }, [fullBranches, dispatch, loading, inScope, withStaff, isAgentMode]);

  useEffect(() => {
    if (isAgentMode) {
      loadPage(1, '');
    }
  }, [agentId, isAgentMode, loadPage]);

  useEffect(() => {
    return () => {
      debouncedSearch.cancel();
      abortRef.current?.abort();
    };
  }, [debouncedSearch]);

  const servicesTreeData = useMemo(
    () =>
      (fullBranches || [])
        .map((branch) => ({
          title: branch.name,
          value: prefixBranchValue(branch.id),
          key: prefixBranchValue(branch.id),
          selectable: false,
          children:
            branch?.services
              ?.filter(
                (s: any) =>
                  !s.deleted_at && !(filterServices || []).includes(`${s.id}`),
              )
              .map((service: any) => ({
                title: service.service_type?.name,
                value: prefixServiceValue(service.id),
                key: prefixServiceValue(service.id),
                tagLabel: `${branch.name} - ${service.service_type?.name}`,
              })) || [],
        }))
        .filter((branch) => branch.children.length > 0),
    [fullBranches, filterServices],
  );

  const getServiceById = useCallback(
    (serviceId: string | number) => {
      const normalizedServiceId = unprefixValue(serviceId);
      let serviceBranch: any = null;

      const service =
        (fullBranches || [])
          .map((branch: any) =>
            branch?.services?.find((s: any) => {
              if (`${s.id}` === `${normalizedServiceId}`) {
                serviceBranch = branch;
                return true;
              }
              return false;
            }),
          )
          .find(Boolean) || null;

      return service
        ? {
            ...service,
            branch: serviceBranch,
          }
        : null;
    },
    [fullBranches],
  );

  const handleSelectionChange = useCallback(
    (nextValue: any, ...rest: any[]) => {
      const normalizedValue = normalizeOutputValue(nextValue);

      onChange?.(normalizedValue);
      fieldProps?.onChange?.(normalizedValue, ...rest);
      selectProps?.onChange?.(normalizedValue, ...rest);
    },
    [onChange, fieldProps, selectProps],
  );

  const mergedTreeSelectProps: any = {
    ...fieldProps,
    ...selectProps,
  };

  if (hasRemoteSearch) {
    mergedTreeSelectProps.onSearch = handleSearch;
  }

  return (
    <ProFormItem
      name={name}
      label={label}
      tooltip={tooltip}
      valuePropName="value"
      required={required}
      getValueFromEvent={(value: any) => normalizeOutputValue(value)}
      getValueProps={(formValue: any) => ({
        value: normalizeInputValue(formValue),
      })}
      {...props}
    >
      <TreeSelect
        allowClear
        treeNodeFilterProp="title"
        popupRender={(element) => (
          <Spin spinning={localLoading || loading}>{element}</Spin>
        )}
        showSearch
        loading={localLoading || loading}
        onChange={handleSelectionChange}
        {...mergedTreeSelectProps}
        onDropdownVisibleChange={handleDropdownVisibleChange}
        tagRender={(tagProps) => {
          const service = getServiceById(tagProps.value);

          if (!service) {
            return (
              <Tag>
                <Spin size="small" />
              </Tag>
            );
          }

          return (
            <Tag {...tagProps}>
              {service?.branch?.name} - {service?.service_type?.name}
            </Tag>
          );
        }}
        treeData={servicesTreeData}
        treeCheckable
        placeholder={
          placeholder || (
            <F entity={INTL_ENTITY.BRANCH} action={INTL_ACTION.ALL} />
          )
        }
        style={{ width: '100%' }}
        dropdownStyle={{ maxHeight: 400, overflow: 'auto' }}
      />
    </ProFormItem>
  );
};

export default BranchServiceTreeSelect;
